import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Select from "react-select";
import { TAB_OPTIONS } from "../../constants/constants";
import { MdDragIndicator } from "react-icons/md";

/* ---------------- DRAG ITEM ---------------- */

const SortableItem = ({ id, label }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        padding: "4px 10px", marginBottom: "8px",
        background: "#f5f5f5",
        border: "1px solid #ddd",
        borderRadius: "3px",
        cursor: "grab", width: "100%"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {label}
        </div>
    );
};

/* ---------------- MAIN PANEL ---------------- */

const InsightPanel = ({
    onChange,
    insightConfig,
    reportConfiguration,
    setReportConfiguration
}) => {
    const [open, setOpen] = useState(false);
    const [scoreWidgets, setScoreWidgets] = useState(true);
    const [speedometer, setSpeedometer] = useState(true);
    const [selectedTabs, setSelectedTabs] = useState([]);
    const [tabOrder, setTabOrder] = useState([]);
    const [averageToggle, setAverageToggle] = useState(false);

    useEffect(() => {
        if (!onChange) return;

        onChange({
            score_widgets: scoreWidgets,
            speedometer,
            tabs: tabOrder.map((tab, index) => ({
                key: tab.key,
                label: tab.label,
                sequence: index + 1
            }))
        });

        if (setReportConfiguration) {
            setReportConfiguration(prev => ({
                ...prev,
                average_toggle: averageToggle
            }));
        }

    }, [scoreWidgets, speedometer, tabOrder, averageToggle]);

    /* -------- RESTORE INSIGHT FROM API -------- */

    useEffect(() => {
        if (!insightConfig) return;
        setScoreWidgets(!!insightConfig.score_widgets);
        setSpeedometer(!!insightConfig.speedometer);
        setAverageToggle(!!reportConfiguration?.average_toggle);
        if (Array.isArray(insightConfig.tabs) && insightConfig.tabs.length > 0) {
            const mappedTabs = insightConfig.tabs.map(tab => ({
                key: tab.key,
                label: tab.label
            }));
            setTabOrder(mappedTabs);
            setSelectedTabs(
                mappedTabs.map(tab => ({
                    label: tab.label,
                    value: tab.key
                }))
            );
        } else {
            setTabOrder([]);
            setSelectedTabs([]);
        }
    }, []);

    const handleTabSelect = (value) => {
        setSelectedTabs(value || []);
        setTabOrder(
            (value || []).map(v => ({
                key: v.value,
                label: v.label
            }))
        );
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setTabOrder(items => {
            const oldIndex = items.findIndex(t => t.key === active.id);
            const newIndex = items.findIndex(t => t.key === over.id);
            return arrayMove(items, oldIndex, newIndex);
        });
    };

    return (
        <div className="insight-panel">
            <div
                className="d-flex justify-content-between align-items-center insight-header"
                onClick={() => setOpen(!open)}
                style={{
                    cursor: "pointer",
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    background: "#fafafa",
                }}>
                <strong>Insight Configuration</strong>
                <span>{open ? "▲" : "▼"}</span>
            </div>

            {open && (
                <div
                    className="insight-body"
                    style={{
                        border: "1px solid #ddd",
                        borderTop: "none",
                        padding: "12px",
                        borderRadius: "0 0 6px 6px",
                        background: "#fff",
                    }}>
                    {/* <Form.Check
                        type="checkbox"
                        label="Score Widgets"
                        checked={scoreWidgets}
                        onChange={(e) => setScoreWidgets(e.target.checked)}
                    /> */}

                    {/* 🔹 Average Toggle → ReportConfiguration */}
                    <Form.Check
                        type="checkbox"
                        label="Average Toggle"
                        checked={averageToggle}
                        onChange={(e) => setAverageToggle(e.target.checked)}
                        className="mt-2" />

                    <Form.Check
                        type="checkbox"
                        label="Speedometer"
                        checked={speedometer}
                        onChange={(e) => setSpeedometer(e.target.checked)}
                        className="mb-3" />

                    <Select
                        isMulti
                        options={TAB_OPTIONS}
                        value={selectedTabs}
                        placeholder="Select Insight Tabs"
                        onChange={handleTabSelect} />

                    {tabOrder?.length > 0 && (
                        <div className="mt-3">
                            <DndContext
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}>
                                <SortableContext
                                    items={tabOrder.map(t => t.key)}
                                    strategy={verticalListSortingStrategy}>
                                    {tabOrder.map(tab => (
                                        <div
                                            className="d-flex align-items-center w-100"
                                            key={tab.key}>
                                            <MdDragIndicator />
                                            <SortableItem
                                                id={tab.key}
                                                label={tab.label} />
                                        </div>
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </div>)}
                </div>)}
        </div>
    );
};

export default InsightPanel;
